<script setup>
// Auditoria → Avaliações: o resumo do período, o ranking de qualidade por
// atendente e a lista das avaliações, com o detalhe da escolhida ao lado.
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import AvaliacaoDetalhe from './AvaliacaoDetalhe.vue';
import { createGestaoApi, nota, quando } from './api';
import messages from './i18n';

const props = defineProps({
  accountId: { type: [Number, String], required: true },
  // O período escolhido no topo da Auditoria.
  dias: { type: Number, default: 7 },
});

// Ajuste de nota: o topo da Auditoria recarrega os números.
const emit = defineEmits(['mudou']);

const { t } = useI18n({ messages });
const api = computed(() => createGestaoApi(props.accountId));

const dias = computed(() => props.dias);
const atendente = ref('');
const filtro = ref('');
const dados = ref(null);
const itens = ref([]);
const pagina = ref(1);
const carregando = ref(true);
const erro = ref(false);

const escolhida = ref(null);
const detalhe = ref(null);
const carregandoDetalhe = ref(false);
const salvando = ref(false);
const erroDoAjuste = ref('');
const aviso = ref('');

const carrega = async (mais = false) => {
  carregando.value = true;
  erro.value = false;
  try {
    const { data } = await api.value.avaliacoes({
      dias: dias.value,
      user_id: atendente.value || undefined,
      filtro: filtro.value || undefined,
      pagina: mais ? pagina.value + 1 : 1,
    });
    dados.value = data;
    pagina.value = data.lista.pagina;
    itens.value = mais ? [...itens.value, ...data.lista.itens] : data.lista.itens;
  } catch {
    erro.value = true;
  } finally {
    carregando.value = false;
  }
};

const abre = async id => {
  escolhida.value = id;
  aviso.value = '';
  erroDoAjuste.value = '';
  carregandoDetalhe.value = true;
  try {
    const { data } = await api.value.avaliacao(id);
    if (escolhida.value === id) detalhe.value = data;
  } catch {
    detalhe.value = null;
  } finally {
    carregandoDetalhe.value = false;
  }
};

const ajusta = async dadosDoAjuste => {
  salvando.value = true;
  erroDoAjuste.value = '';
  try {
    const { data } = await api.value.ajusta(escolhida.value, dadosDoAjuste);
    detalhe.value = { ...data, criterios_nomes: detalhe.value.criterios_nomes };
    carrega();
    emit('mudou');
  } catch (e) {
    erroDoAjuste.value = e.response?.data?.erro || t('NEUXS_GESTAO.ERRO');
  } finally {
    salvando.value = false;
  }
};

const refaz = async () => {
  try {
    await api.value.refaz(escolhida.value);
    aviso.value = t('NEUXS_GESTAO.DETALHE.REFAZENDO');
  } catch (e) {
    aviso.value = e.response?.data?.erro || t('NEUXS_GESTAO.ERRO');
  }
};

watch([dias, atendente, filtro], () => carrega());
onMounted(() => carrega());

const colunas = computed(() =>
  Object.entries(dados.value?.criterios || {}).map(([chave, nome]) => ({ chave, nome }))
);
const temMais = computed(
  () => dados.value && itens.value.length < dados.value.lista.total
);
const baixa = valor => valor !== null && valor !== undefined && valor < 6;
</script>

<template>
  <div class="flex flex-col gap-5">
    <div v-if="carregando && !dados" class="flex justify-center py-10">
      <Spinner />
    </div>
    <div v-else-if="erro && !dados" class="flex items-center gap-3 text-sm text-n-ruby-11">
      {{ t('NEUXS_GESTAO.ERRO') }}
      <Button size="sm" variant="faded" :label="t('NEUXS_GESTAO.TENTAR')" @click="carrega()" />
    </div>

    <template v-else-if="dados">
      <p
        v-if="!dados.ativa"
        class="p-3 m-0 text-sm rounded-xl bg-n-amber-2 text-n-amber-11"
      >
        {{ t('NEUXS_GESTAO.DESLIGADA') }}
      </p>

      <section class="flex flex-col gap-2">
        <h2 class="m-0 text-heading-3 text-n-slate-12">{{ t('NEUXS_GESTAO.RANKING') }}</h2>
        <p v-if="!dados.ranking.length" class="m-0 text-sm text-n-slate-11">
          {{ t('NEUXS_GESTAO.VAZIO') }}
        </p>
        <div v-else class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-xs text-left text-n-slate-11">
                <th class="py-2 pe-3 font-normal">{{ t('NEUXS_GESTAO.ATENDENTE') }}</th>
                <th class="py-2 pe-3 font-normal text-end">{{ t('NEUXS_GESTAO.NOTA') }}</th>
                <th
                  v-for="coluna in colunas"
                  :key="coluna.chave"
                  class="py-2 pe-3 font-normal text-end whitespace-nowrap"
                >
                  {{ coluna.nome }}
                </th>
                <th class="py-2 font-normal text-end">{{ t('NEUXS_GESTAO.AVALIADAS') }}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr
                v-for="linha in dados.ranking"
                :key="linha.user_id"
                class="cursor-pointer hover:bg-n-alpha-1"
                @click="atendente = String(linha.user_id)"
              >
                <td class="py-2 pe-3 text-n-slate-12">{{ linha.nome }}</td>
                <td class="py-2 pe-3 text-end">
                  <span
                    class="px-2 py-0.5 rounded-md tabular-nums"
                    :class="baixa(linha.nota) ? 'bg-n-ruby-3 text-n-ruby-11' : 'bg-n-blue-3 text-n-blue-11'"
                  >
                    {{ nota(linha.nota) }}
                  </span>
                </td>
                <td
                  v-for="coluna in colunas"
                  :key="coluna.chave"
                  class="py-2 pe-3 text-end tabular-nums"
                  :class="baixa(linha.criterios[coluna.chave]) ? 'text-n-ruby-11' : 'text-n-slate-11'"
                >
                  {{ nota(linha.criterios[coluna.chave]) }}
                </td>
                <td class="py-2 text-end tabular-nums text-n-slate-11">{{ linha.avaliadas }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <div class="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)] items-start">
        <section class="flex flex-col gap-3">
          <h2 class="m-0 text-heading-3 text-n-slate-12">{{ t('NEUXS_GESTAO.LISTA') }}</h2>
          <div class="flex flex-wrap gap-2">
            <select v-model="atendente" class="!mb-0 !w-auto">
              <option value="">{{ t('NEUXS_GESTAO.TODOS') }}</option>
              <option v-for="linha in dados.ranking" :key="linha.user_id" :value="String(linha.user_id)">
                {{ linha.nome }}
              </option>
            </select>
            <select v-model="filtro" class="!mb-0 !w-auto">
              <option value="">{{ t('NEUXS_GESTAO.FILTROS.TODAS') }}</option>
              <option value="baixa">{{ t('NEUXS_GESTAO.FILTROS.BAIXA') }}</option>
              <option value="alerta">{{ t('NEUXS_GESTAO.FILTROS.ALERTA') }}</option>
            </select>
          </div>
          <p v-if="!itens.length" class="m-0 text-sm text-n-slate-11">{{ t('NEUXS_GESTAO.VAZIO') }}</p>
          <ul v-else class="flex flex-col gap-1.5 p-0 m-0 list-none">
            <li v-for="item in itens" :key="item.id">
              <button
                type="button"
                class="grid w-full grid-cols-[1fr_auto] gap-x-3 gap-y-0.5 px-3 py-2 text-start rounded-lg border"
                :class="escolhida === item.id ? 'border-n-blue-9 bg-n-alpha-1' : 'border-transparent bg-n-alpha-1 hover:bg-n-alpha-2'"
                @click="abre(item.id)"
              >
                <span class="text-sm truncate text-n-slate-12">
                  <strong class="font-medium">{{ item.atendente }}</strong>
                  · {{ item.cliente || '—' }}
                </span>
                <span
                  class="row-span-2 self-center px-2 py-0.5 rounded-md text-sm tabular-nums"
                  :class="baixa(item.nota) ? 'bg-n-ruby-3 text-n-ruby-11' : 'bg-n-blue-3 text-n-blue-11'"
                >
                  {{ nota(item.nota) }}
                  <span v-if="item.ajustada" class="text-xs">*</span>
                </span>
                <span class="text-xs truncate text-n-slate-11">
                  {{ quando(item.avaliada_em) }}
                  <template v-if="item.assunto"> · {{ item.assunto }}</template>
                  <span v-if="item.alertas.length" class="text-n-ruby-11">
                    · ⚠ {{ item.alertas.map(a => t(`NEUXS_GESTAO.ALERTAS.${a}`)).join(', ') }}
                  </span>
                </span>
              </button>
            </li>
          </ul>
          <Button
            v-if="temMais"
            size="sm"
            variant="faded"
            color="slate"
            :label="t('NEUXS_GESTAO.MAIS')"
            :is-loading="carregando"
            @click="carrega(true)"
          />
        </section>

        <section class="p-4 border rounded-xl border-n-weak">
          <div v-if="carregandoDetalhe" class="flex justify-center py-10"><Spinner /></div>
          <p v-else-if="!detalhe" class="m-0 text-sm text-n-slate-11">{{ t('NEUXS_GESTAO.ESCOLHA') }}</p>
          <template v-else>
            <p v-if="aviso" class="p-2 m-0 mb-3 text-sm rounded-lg bg-n-alpha-2 text-n-slate-11">{{ aviso }}</p>
            <AvaliacaoDetalhe
              :avaliacao="detalhe"
              :account-id="accountId"
              :salvando="salvando"
              :erro="erroDoAjuste"
              pode-ajustar
              @ajustar="ajusta"
              @refazer="refaz"
            />
          </template>
        </section>
      </div>
    </template>
  </div>
</template>
